import type { PhotoCountResponse, PhotoResponse, PhotosResponse } from '@nargus/shared';
import { download, request, upload } from '../http';

export interface PhotoRef {
  organizationId: string;
  photoId: string;
}

export interface UploadPhotoBody {
  organizationId: string;
  file: File;
  thumbnail?: Blob;
  takenAt?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  albumId?: string;
}

export interface UpdatePhotoBody extends PhotoRef {
  caption?: string | null;
  favorite?: boolean;
  takenAt?: string;
}

export interface UpdatePhotosBody {
  organizationId: string;
  photoIds: string[];
  favorite?: boolean;
  addAlbumId?: string;
  removeAlbumId?: string;
}

export interface DeletePhotosBody {
  organizationId: string;
  photoIds: string[];
}

function path(organizationId: string, albumId?: string): string {
  const base = `/organizations/${organizationId}/photos`;
  return albumId ? `${base}?albumId=${encodeURIComponent(albumId)}` : base;
}

export const photoResource = {
  list: (organizationId: string, albumId?: string): Promise<PhotosResponse> =>
    request(path(organizationId, albumId)),

  add: ({ organizationId, file, thumbnail, ...meta }: UploadPhotoBody): Promise<PhotoResponse> => {
    const body = new FormData();
    body.append('photo', file);
    if (thumbnail) body.append('thumb', thumbnail, 'thumb.jpg');
    if (meta.takenAt) body.append('takenAt', meta.takenAt);
    if (meta.width) body.append('width', String(meta.width));
    if (meta.height) body.append('height', String(meta.height));
    if (meta.durationMs !== undefined) body.append('durationMs', String(meta.durationMs));
    if (meta.albumId) body.append('albumId', meta.albumId);
    return upload(path(organizationId), body);
  },

  update: ({ organizationId, photoId, ...body }: UpdatePhotoBody): Promise<PhotoResponse> =>
    request(`/organizations/${organizationId}/photos/${photoId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  updateMany: ({
    organizationId,
    ...body
  }: UpdatePhotosBody): Promise<PhotoCountResponse> =>
    request(path(organizationId), { method: 'PATCH', body: JSON.stringify(body) }),

  removeMany: ({
    organizationId,
    ...body
  }: DeletePhotosBody): Promise<PhotoCountResponse> =>
    request(path(organizationId), { method: 'DELETE', body: JSON.stringify(body) }),

  remove: ({ organizationId, photoId }: PhotoRef): Promise<void> =>
    request(`/organizations/${organizationId}/photos/${photoId}`, { method: 'DELETE' }),

  get: ({ organizationId, photoId }: PhotoRef): Promise<Blob> =>
    download(`/organizations/${organizationId}/photos/${photoId}`),

  getThumbnail: ({ organizationId, photoId }: PhotoRef): Promise<Blob> =>
    download(`/organizations/${organizationId}/photos/${photoId}?size=thumb`),
};
